import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance as axios } from '../context/AuthContext';
import { FaArrowLeft, FaStar, FaTrash, FaChartBar } from 'react-icons/fa';
import { toast } from 'sonner';

const API = '';

export default function FeedbackDashboard() {
  const [loading, setLoading] = useState(true);
  const [feedbackData, setFeedbackData] = useState({ feedback: [], summary: {} });

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const response = await axios.get(`${API}/api/feedback`, { withCredentials: true });
      setFeedbackData(response.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (feedbackId) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await axios.delete(`${API}/api/feedback/${feedbackId}`, { withCredentials: true });
      toast.success('Feedback deleted');
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to delete feedback');
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FaStar key={i} className={i < rating ? 'text-yellow-400' : 'text-gray-300'} />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const { feedback, summary } = feedbackData;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="feedback-dashboard">
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Link to="/admin/professionals" className="flex items-center space-x-2 text-gray-600 hover:text-purple-600" data-testid="feedback-back-to-admin">
              <FaArrowLeft />
              <span>Back to Admin Panel</span>
            </Link>
            <span className="text-gray-300">|</span>
            <h1 className="text-2xl font-bold text-gray-800">Beta Tester Feedback</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Responses</p>
                <p className="text-3xl font-bold text-gray-800">{summary.total_responses || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <FaChartBar className="text-purple-600 text-xl" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Average Rating</p>
                <div className="flex items-center space-x-2">
                  <p className="text-3xl font-bold text-gray-800">{summary.average_rating || 0}</p>
                  <FaStar className="text-yellow-400 text-xl" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 md:col-span-2">
            <p className="text-gray-500 text-sm mb-3">Rating Distribution</p>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(star => {
                const count = summary.rating_distribution?.[star] || 0;
                const percent = summary.total_responses > 0 ? (count / summary.total_responses * 100) : 0;
                return (
                  <div key={star} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 w-8">{star}★</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-yellow-400 rounded-full h-3 transition-all"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-600 w-8">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Feedback List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-6 border-b">
            <h2 className="text-lg font-bold text-gray-800">All Feedback</h2>
          </div>
          
          {feedback.length === 0 ? (
            <div className="p-12 text-center">
              <FaStar className="text-gray-300 text-6xl mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700">No feedback yet</h3>
              <p className="text-gray-500">Invite your family members to test the app and share their thoughts!</p>
            </div>
          ) : (
            <div className="divide-y">
              {feedback.map((item) => (
                <div key={item.feedback_id} className="p-6 hover:bg-gray-50" data-testid={`feedback-item-${item.feedback_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="flex">{renderStars(item.rating)}</div>
                        <span className="text-sm text-gray-500">{item.user_name}</span>
                        <span className="text-sm text-gray-400">
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                        {item.page_context && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {item.page_context}
                          </span>
                        )}
                      </div>
                      
                      {item.liked && (
                        <div className="mb-2">
                          <span className="text-xs font-semibold text-green-600 uppercase">Liked:</span>
                          <p className="text-gray-700">{item.liked}</p>
                        </div>
                      )}
                      
                      {item.confusing && (
                        <div className="mb-2">
                          <span className="text-xs font-semibold text-amber-600 uppercase">Confusing:</span>
                          <p className="text-gray-700">{item.confusing}</p>
                        </div>
                      )}
                      
                      {item.suggestions && (
                        <div>
                          <span className="text-xs font-semibold text-blue-600 uppercase">Suggestions:</span>
                          <p className="text-gray-700">{item.suggestions}</p>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => handleDelete(item.feedback_id)}
                      className="flex items-center gap-1 px-2 py-1.5 sm:p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition text-xs sm:text-sm"
                      title="Delete feedback"
                    >
                      <FaTrash />
                      <span className="sm:hidden">Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
